<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

withDefaults(
    defineProps<{
        results?: Record<string, unknown> | null;
        loading?: boolean;
        title?: string;
    }>(),
    {
        results: null,
        loading: false,
        title: 'Smoke Test Results',
    },
);

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();

const expandedIndex = ref<number | null>(null);

function toggle(idx: number) {
    expandedIndex.value = expandedIndex.value === idx ? null : idx;
}

const statusIcon = (status: string) => {
    if (status === 'pass') return 'check_circle';
    if (status === 'fail') return 'cancel';
    return 'warning';
};

const statusClass = (status: string) => {
    if (status === 'pass') return 'smoketest-status-pass';
    if (status === 'fail') return 'smoketest-status-fail';
    return 'smoketest-status-warning';
};
</script>

<template>
    <div class="smoketest-results-panel">
        <div class="smoketest-results-header">
            <span class="smoketest-results-title">{{ title }}</span>
            <button class="btn btn-sm btn-secondary" @click="emit('close')">{{ t('typeManagement.smokeTest.dismiss') }}</button>
        </div>

        <div v-if="loading" class="smoketest-results-loading">{{ t('typeManagement.smokeTest.running') }}</div>

        <template v-else-if="results">
            <div
                :class="[
                    'smoketest-overall-banner',
                    (results as any).overall_status === 'pass' ? 'pass' : 'fail',
                ]"
            >
                <span class="material-icons" style="font-size: 18px; margin-right: 6px">
                    {{ (results as any).overall_status === 'pass' ? 'check_circle' : 'cancel' }}
                </span>
                {{
                    (results as any).overall_status === 'pass'
                        ? t('typeManagement.smokeTest.allPassed')
                        : t('typeManagement.smokeTest.someFailed')
                }}
                <span
                    v-if="(results as any).sample_count != null"
                    style="margin-left: auto; font-size: 0.8rem; opacity: 0.8"
                >
                    {{ t('typeManagement.smokeTest.sampleRecords', { count: (results as any).sample_count }, (results as any).sample_count) }}
                </span>
            </div>

            <div class="smoketest-results-list">
                <div
                    v-for="(r, idx) in (results as any).results"
                    :key="idx"
                    class="smoketest-result-item"
                >
                    <div class="smoketest-result-row" @click="r.traceback ? toggle(idx) : null">
                        <span
                            :class="['material-icons', statusClass(r.status)]"
                            style="font-size: 18px"
                        >
                            {{ statusIcon(r.status) }}
                        </span>
                        <span class="smoketest-result-component">{{ r.component }}</span>
                        <span class="smoketest-result-message">{{ r.message }}</span>
                        <span
                            v-if="r.traceback"
                            class="material-icons smoketest-expand-icon"
                            style="font-size: 16px; cursor: pointer; margin-left: auto"
                        >
                            {{ expandedIndex === idx ? 'expand_less' : 'expand_more' }}
                        </span>
                    </div>

                    <pre v-if="r.traceback && expandedIndex === idx" class="smoketest-traceback">{{
                        r.traceback
                    }}</pre>
                </div>
            </div>
        </template>
    </div>
</template>
